import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Users, FileText, Clock, DollarSign, CheckCircle, XCircle, Zap, TrendingDown, ShieldCheck, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';

const ProcessPage = () => {
  const regulatedProcess = [
    { step: 1, title: 'Single Utility Provider', description: 'You have one utility company assigned to your area with no alternatives.', icon: <Building2 size={20} /> },
    { step: 2, title: 'Fixed Rates Set by Regulators', description: 'Rates are determined by state regulators, not market competition.', icon: <FileText size={20} /> },
    { step: 3, title: 'No Negotiation Power', description: 'You accept whatever rate the utility charges - take it or leave it.', icon: <XCircle size={20} /> },
    { step: 4, title: 'Limited Transparency', description: 'Complex bills with bundled charges make it hard to understand true costs.', icon: <Clock size={20} /> },
    { step: 5, title: 'Pay Standard Rates', description: 'Everyone pays the same regulated rate regardless of usage patterns.', icon: <DollarSign size={20} /> }
  ];

  const deregulatedProcess = [
    { step: 1, title: 'Free Consultation with Wattson', description: 'Chat with our AI advisor or speak with an expert to understand your options.', icon: <Zap size={20} /> },
    { step: 2, title: 'Upload Your Utility Bill', description: 'Share your current bill so we can analyze your usage and identify savings.', icon: <FileText size={20} /> },
    { step: 3, title: 'We Shop Multiple Suppliers', description: 'We leverage our relationships with dozens of suppliers to find your best rate.', icon: <Users size={20} /> },
    { step: 4, title: 'Review Your Custom Proposal', description: 'See exactly how much you\'ll save with a transparent, side-by-side comparison.', icon: <TrendingDown size={20} /> },
    { step: 5, title: 'Lock In Your Savings', description: 'Sign a simple contract and start saving 10-30% on your energy bills.', icon: <CheckCircle size={20} /> }
  ];

  const benefits = [
    { title: 'No Cost to You', description: 'Our service is completely free. Suppliers pay us, not you.', icon: <DollarSign size={28} /> },
    { title: 'Vendor Neutral', description: 'We work with all major suppliers to find YOUR best deal.', icon: <ShieldCheck size={28} /> },
    { title: 'Ongoing Support', description: 'We monitor rates and alert you to better opportunities.', icon: <Handshake size={28} /> }
  ];

  return (
    <main className="page-content" data-testid="process-page">
      {/* Page Header */}
      <section className="page-header-with-image process-header">
        <div className="page-header-overlay"></div>
        <div className="container page-header-content">
          <span className="overline">How It Works</span>
          <h1 className="page-title" style={{marginTop: '0.5rem'}}>Our Process</h1>
          <p className="page-subtitle">
            See the difference between buying energy in a regulated market vs. working with Last Mile Advisors in a deregulated market.
          </p>
        </div>
      </section>

      {/* Side by Side Comparison */}
      <section className="section section-light">
        <div className="container">
          <div className="process-comparison">
            {/* Regulated Market Column */}
            <div className="process-column process-regulated" data-testid="regulated-column">
              <div className="process-column-header regulated-header">
                <XCircle size={28} className="process-header-icon" />
                <h2>Regulated Market</h2>
                <p>The old way - no choice, no savings</p>
              </div>
              <div className="process-steps">
                {regulatedProcess.map((item) => (
                  <div key={item.step} className="process-step regulated-step">
                    <div className="process-step-number regulated-number">{item.step}</div>
                    <div className="process-step-content">
                      <div className="process-step-icon regulated-icon">{item.icon}</div>
                      <div className="process-step-text">
                        <h3>{item.title}</h3>
                        <p>{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="process-result regulated-result">
                <XCircle size={20} />
                <span>You pay whatever they charge</span>
              </div>
            </div>

            {/* VS Divider */}
            <div className="process-vs">
              <span>VS</span>
            </div>

            {/* Deregulated Market Column */}
            <div className="process-column process-deregulated" data-testid="deregulated-column">
              <div className="process-column-header deregulated-header">
                <CheckCircle size={28} className="process-header-icon" />
                <h2>With Last Mile Advisors</h2>
                <p>The smart way - choice, savings, support</p>
              </div>
              <div className="process-steps">
                {deregulatedProcess.map((item) => (
                  <div key={item.step} className="process-step deregulated-step">
                    <div className="process-step-number deregulated-number">{item.step}</div>
                    <div className="process-step-content">
                      <div className="process-step-icon deregulated-icon">{item.icon}</div>
                      <div className="process-step-text">
                        <h3>{item.title}</h3>
                        <p>{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="process-result deregulated-result">
                <CheckCircle size={20} />
                <span>Save 10-30% on your energy bills</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <span className="overline">Benefits</span>
            <h2 className="section-title">Why Work With Us?</h2>
          </div>
          <div className="process-benefits">
            {benefits.map((benefit, index) => (
              <div key={index} className="process-benefit" data-testid={`benefit-${index}`}>
                <div className="process-benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-cta-hero">
        <div className="section-cta-overlay"></div>
        <div className="container section-cta-content">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Start Saving?</h2>
            <p className="cta-subtitle">
              It takes just 5 minutes to see how much you could save. Chat with Wattson or request a free consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="btn-primary" data-testid="process-consultation-btn">
                  Request Consultation
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/market-map">
                <Button size="lg" className="btn-outline-light" data-testid="process-check-state-btn">
                  Check Your State
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProcessPage;
